import React, { ReactNode, useEffect } from "react";
import { AuthProvider, useAuth } from "@/services/auth/AuthContext";
import { ActiveAccountGate } from "@/services/auth/ActiveAccountGate";
import ScreenLoader from "@/components/shared/ScreenLoader";
import { useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { useAccount } from "@/entities/accounts/hooks";
import { useUsersMe } from "@/entities/users/hooks";

const AuthHandler: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, login } = useAuth();
  const location = useLocation();
  const { data: accountData, isSuccess: isAccountDataSuccess } = useAccount();
  const { data: userData, isSuccess: isUserSuccess } = useUsersMe();

  useEffect(() => {
    if (!isAuthenticated) {
      if (location.pathname !== "/") {
        const redirectPath = `${location.pathname}${location.search}`;
        window.localStorage.setItem(
          "FRONTEGG_AFTER_AUTH_REDIRECT_URL",
          redirectPath
        );
      }
      login();
    }
  }, [isAuthenticated, login, location]);

  // Set Sentry user context when user data is loaded
  useEffect(() => {
    if (isUserSuccess && isAccountDataSuccess) {
      Sentry.setUser({
        id: userData?.id
      });

      Sentry.setTag("accountId", accountData?.id);
      Sentry.setExtra("account_id", accountData?.id);
    }
  }, [isUserSuccess, userData, accountData]);

  if (!isAuthenticated) {
    return <ScreenLoader />;
  }

  return <>{children}</>;
};

const AuthWrapper: React.FC<{ children: ReactNode }> = ({ children }) => (
  <AuthProvider>
    <ActiveAccountGate>
      <AuthHandler>{children}</AuthHandler>
    </ActiveAccountGate>
  </AuthProvider>
);

export default AuthWrapper;
